import { Checkbox, Flex, ListItem, OrderedList, Spinner } from "@chakra-ui/react";
import { Step, Steps } from "chakra-ui-steps";
import { useEffect, useMemo, useRef, useState } from "react";
import { RpcError } from "grpc-web";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import usePermission from "../../../hooks/usePermission";
import useClaimsToast from "../../../hooks/useToast";

// Providers
import { useClaimDetails } from "../../../context-providers/claims-details-provider";

// Protos
import {
	ChecklistType,
	GetChecklistTemplateRequest,
	GetChecklistTemplateResponse
} from "../../../utils/api/protos/messages/claims.message_pb";

// Utils
import { ChecklistItem, User } from "../../../utils/types";
import { ClaimsBFFControllerClient } from "../../../utils/api/protos/Claims-bffServiceClientPb";
import { getBffRequest } from "../../../utils/api/base/api";
import { getApiToken } from "../../../utils/helpers";

/** Initial steps to later inject values from the server */
type ChecklistStepType = { label: string; items: { type: ChecklistType; label: string }[] };
type ChecklistTemplateData = { label: string; checkListType: number };

const hydrateChecklistTemplate = (templateData: ChecklistTemplateData[]): ChecklistStepType[] => {
	const getTypeLabel = (type: ChecklistType): string => {
		const found = templateData.find((element) => element.checkListType === type);
		return found ? found.label : "could not find label";
	};

	return [
		{
			label: "Eligibility",
			items: [
				{
					type: ChecklistType.ELIGIBILITY_ACTIVE,
					label: getTypeLabel(ChecklistType.ELIGIBILITY_ACTIVE)
				},
				{
					type: ChecklistType.ELIGIBILITY_SINGLE_OR_FAMILY,
					label: getTypeLabel(ChecklistType.ELIGIBILITY_SINGLE_OR_FAMILY)
				},
				{
					type: ChecklistType.ELIGIBILITY_ELIGIBLE,
					label: getTypeLabel(ChecklistType.ELIGIBILITY_ELIGIBLE)
				},
				{ type: ChecklistType.ELIGIBILITY_DEPENDENT, label: getTypeLabel(ChecklistType.ELIGIBILITY_DEPENDENT) },
				{ type: ChecklistType.ELIGIBILITY_PREMIUMS, label: getTypeLabel(ChecklistType.ELIGIBILITY_PREMIUMS) },
				{
					type: ChecklistType.ELIGIBILITY_PREVIOUS_PAYMENT,
					label: getTypeLabel(ChecklistType.ELIGIBILITY_PREVIOUS_PAYMENT)
				}
			]
		},
		{
			label: "Invoices",
			items: [
				{ type: ChecklistType.INVOICES_REQUIREMENTS, label: getTypeLabel(ChecklistType.INVOICES_REQUIREMENTS) },
				{ type: ChecklistType.INVOICES_ELIGIBLE_DATES, label: getTypeLabel(ChecklistType.INVOICES_ELIGIBLE_DATES) },
				{
					type: ChecklistType.INVOICES_HOURS_BILLED,
					label: getTypeLabel(ChecklistType.INVOICES_HOURS_BILLED)
				},
				{
					type: ChecklistType.INVOICES_NON_ALLOWABLE_CHARGES,
					label: getTypeLabel(ChecklistType.INVOICES_NON_ALLOWABLE_CHARGES)
				},
				{ type: ChecklistType.INVOICES_PLAN_MAXIMUM, label: getTypeLabel(ChecklistType.INVOICES_PLAN_MAXIMUM) }
			]
		},
		{
			label: "Supporting Documents",
			items: [
				{
					type: ChecklistType.DOCS_SERVICES_FOR_ELIGIBLE,
					label: getTypeLabel(ChecklistType.DOCS_SERVICES_FOR_ELIGIBLE)
				},
				{
					type: ChecklistType.DOCS_BEST_EVIDENCE_BACKUP,
					label: getTypeLabel(ChecklistType.DOCS_BEST_EVIDENCE_BACKUP)
				},
				{
					type: ChecklistType.DOCS_INVOICE_DATE_CHECK,
					label: getTypeLabel(ChecklistType.DOCS_INVOICE_DATE_CHECK)
				}
			]
		}
	];
};

export default function Checklist() {
	const { claim, handleUpsertChecklistItem } = useClaimDetails();
	const [checklistIndex, setChecklistIndex] = useState<number | undefined>();
	const [checklistSteps, setCheckListSteps] = useState<ChecklistStepType[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const latestUpdateTime = useRef<number | undefined>();
	const latestUpdateTimeCompleted = useRef<number | undefined>();
	const { getUser } = useAuth();
	const { hasGeneralManagerAccess } = usePermission();
	const toast = useClaimsToast();

	/** Local list of checklist values to be synced with the server */
	const [localChecklist, setLocalChecklist] = useState<{ [type: string]: ChecklistItem }>({});
	let currentUser: User | undefined = getUser();

	/** Load the checklist template */
	useEffect(() => {
		const token = getApiToken();
		const request = new GetChecklistTemplateRequest();
		const client: ClaimsBFFControllerClient = getBffRequest(token);

		setIsLoading(true);
		client
			.getChecklistTemplate(request, null)
			.then((response: GetChecklistTemplateResponse) => {
				const checklistTemplateData = response.getChecklistItemsList().map((item) => ({
					label: item.getLabel(),
					checkListType: item.getChecklistType()
				}));

				const hydratedCheckList = hydrateChecklistTemplate(checklistTemplateData);

				setCheckListSteps(hydratedCheckList);
			})
			.catch((err) => {
				toast("Error", (err as RpcError).message, "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, []);

	/** When the claim from the server changes, update the local checklist */
	useEffect(() => {
		if (latestUpdateTime.current === latestUpdateTimeCompleted.current) {
			const claimChecklistItems: { [type: string]: ChecklistItem } = {};
			claim?.checklistItems?.forEach((item) => (claimChecklistItems[item.type] = item));

			setLocalChecklist(claimChecklistItems);
		}

		if (claim?.checklistItems?.length === 0) {
			// If checklist is empty, set to the first step
			setChecklistIndex(0);
		}
	}, [claim]);

	/** When the claim from the server changes, update the local checklist */
	const stepsCompletion = useMemo(() => {
		// Calculate steps completion
		const localStepsCompletion = checklistSteps.map((step, index) => {
			return step.items.every((item) => localChecklist[item.type]?.value);
		});

		// If the first load of the checklist, set index of first invalid step
		if (checklistIndex === undefined && localChecklist && Object.keys(localChecklist).length && checklistSteps.length) {
			let firstInvalidStep: number | undefined = undefined;
			localStepsCompletion.forEach((stepComplete, index) => {
				if (firstInvalidStep === undefined && !stepComplete) {
					firstInvalidStep = index;
				}
			});
			// If all steps are valid, just show last step
			if (firstInvalidStep === undefined) {
				firstInvalidStep = Math.max(localStepsCompletion.length - 1, 0);
			}

			setChecklistIndex(firstInvalidStep);
		}
		return localStepsCompletion;
	}, [localChecklist, checklistSteps]);

	const isAssignedAnalyst = useMemo(() => {
		return claim?.assignedAnalyst?.["getId"] && claim?.assignedAnalyst.getId() === currentUser?.id;
	}, [claim, currentUser]);

	const handleUpsertChecklist = async (id: number | undefined, type: ChecklistType, label: string, value: boolean) => {
		// Immediately update local value
		setLocalChecklist({ ...localChecklist, [type]: { id, claimId: claim?.claimId, type, label, value } });
		// Update async with the server
		const currentTime = Date.now().valueOf();
		latestUpdateTime.current = currentTime;
		await handleUpsertChecklistItem(id, label, type, value);
		latestUpdateTimeCompleted.current = currentTime;
	};

	return (
		<>
			{isLoading && <Spinner />}
			{!isLoading && checklistSteps.length > 0 && (
				<>
					<Flex>
						<Steps
							activeStep={checklistIndex ?? 0}
							colorScheme="orange"
							onClickStep={setChecklistIndex}
							sx={{
								"& .cui-steps__horizontal-step-container span": {
									opacity: "1",
									color: "gray.800"
								},
								"& .cui-steps__horizontal-step-container [aria-current=step] span": {
									opacity: "1",
									color: "orange.600"
								}
							}}>
							{checklistSteps.map((step, index) => (
								<Step
									opacity={index === checklistIndex ? 1 : 0.8}
									key={index}
									label={step.label}
									isCompletedStep={stepsCompletion[index]}></Step>
							))}
						</Steps>
					</Flex>
					{checklistIndex != null && checklistSteps[checklistIndex] && (
						<OrderedList style={{ columnCount: 2 }} mt="3" mb="3" pl="3" textAlign="left" lineHeight="1.4rem">
							{checklistIndex != null &&
								checklistSteps[checklistIndex].items.map((item) => (
									<ListItem key={item.type} mr="5" ml="4">
										<Checkbox
											left="-39px"
											top="4px"
											w="0px"
											isChecked={localChecklist[item.type]?.value ?? false}
											isDisabled={!hasGeneralManagerAccess && !isAssignedAnalyst}
											colorScheme="orange"
											onChange={() => {
												handleUpsertChecklist(
													localChecklist[item.type]?.id,
													item.type,
													item.label,
													localChecklist[item.type] ? !localChecklist[item.type].value : true
												);
											}}></Checkbox>
										{item.label}
									</ListItem>
								))}
						</OrderedList>
					)}
				</>
			)}
		</>
	);
}
